import Dialog from '../Dialog';
import styled from 'styled-components';
import mixpanel from 'mixpanel-browser';
import { FC, useEffect, useState } from 'react';
import { ActivityData } from './types';
import Restorative from '../../services/Restorative';
import { Player } from '../../services/Restorative/types';
import ActivityAttributionPlayerBank from './ActivityAttributionPlayerBank';
import SelectedPlayerList from './SelectedPlayerList';
import InfoCard from './InfoCard';
import Button from '../Button';
import { getLastActivityExecution } from '../../services/Restorative/getLastActivityExecution';
import { attributeActivityExecution } from '../../services/Restorative/attributeActivityExecution';
import Pagination from '../Pagination';
import ResidentSearch from './ResidentSearch';

import { formatDuration } from '../../utils/FormatDuration';

const MIXPANEL_EVENT = 'Care_CareUI_AssignDurationDialog_View';
const SCREEN_BREAKPOINT = '1100px';
const PLAYERS_PER_PAGE = 5;

const KEEP_GROUP_TIME_LIMIT = 30; //Show the last group by default only if 30 minutes did not pass from the last activity

const Title = styled.div(({ theme }) => {
  return `
    font-size: ${theme.new.type.size[32]};
    margin-bottom: 15px;
    font-weight: bold;

    @media (max-width: ${theme.new.breakpoint.tabletLarge.down}) {
      font-size: ${theme.new.type.size[24]};
      margin-bottom: 10px;
      
    }
  `;
});

const Content = styled.div(({ theme }) => {
  return `
  font-family: ${theme.new.type.face};
  text-align: left;
  min-width: 1000px;
  margin-top: -20px;
  @media (max-width: ${theme.new.breakpoint.tabletLarge.down}) {
    width: 100%;
    min-width: 700px;
  }
`;
});

const Section = styled.div`
  margin-bottom: 30px;

  @media (max-width: ${SCREEN_BREAKPOINT}) {
    margin-bottom: 15px;
  }
`;

const ResidentSection = styled.div`
  margin-bottom: 30px;
`;

const Subsection = styled.div(({ theme }) => {
  return `
  margin-bottom: 20px;
  margin-top: 10px;
  border-bottom: 2px solid ${theme.new.color.neutral[3]};
`;
});

const SubsectionTitle = styled.div(({ theme }) => {
  return `
    font-size: ${theme.new.type.size[14]};
    margin-bottom: 10px;
    color: ${theme.new.color.shade[3]};
  `;
});

const SectionTitle = styled.div(({ theme }) => {
  return `
    font-size: ${theme.new.type.size[20]};
    margin-bottom: 10px;
    font-weight: 500;
    color: ${theme.new.color.shade[3]};

    @media (max-width:  ${theme.new.breakpoint.tabletLarge.down}) {
      font-size: ${theme.new.type.size[16]};
    }
  `;
});

const LoadingMessage = styled.div(({ theme }) => {
  return `
    font-size: ${theme.new.type.size[14]};
  `;
});

const ButtonContainer = styled.div`
  text-align: right;
`;

const PaginationContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
`;

const SkipButton = styled.button(({ theme }) => {
  return `
    font-size: ${theme.new.type.size[14]};
    bacgkround: none;
    border: none;
    cursor: pointer;
    margin-right: 40px;

    transition: 0.2s;

    &:hover {
      color: ${theme.new.color.neutral[7]};
    }
  `;
});

const ClearSelectedButton = styled.button(({ theme }) => {
  const { type } = theme.new;
  return `
    text-decoration: underline;
    font-size: ${type.size[14]};
    border: none;
    background: none;
    margin-left: 40px;
  `;
});

interface ActivityAttributionDialogProps {
  activityData: ActivityData;
  closeDialog: () => void;
}

const ActivityAttributionDialog: FC<ActivityAttributionDialogProps> = ({ activityData, closeDialog }) => {
  const [program, setProgram] = useState(activityData.program);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastActivityLoaded, setLastActivityLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);

  const addPlayerToSelected = (playerId: number) => {
    const newSelectedPlayers = [...selectedPlayers];
    const newPlayers = [...players];
    const playerIndex = newPlayers.findIndex((player) => player.id === playerId);

    if (playerIndex !== -1) {
      const [player] = newPlayers.splice(playerIndex, 1);
      newSelectedPlayers.push(player);
    }

    if (newPlayers.length === 0 && page > 0) {
      setPage(page - 1);
    }
    setSelectedPlayers(newSelectedPlayers);
    setPlayers(newPlayers);
  };

  const clearSelectedPlayers = () => {
    setSelectedPlayers([]);
  };

  const removeSelectedPlayer = (playerId: number) => {
    const newSelectedPlayers = [...selectedPlayers];
    const newPlayers = [...players];
    const playerIndex = selectedPlayers.findIndex((player) => player.id === playerId);

    if (playerIndex !== -1) {
      const [player] = newSelectedPlayers.splice(playerIndex, 1);
      newPlayers.push(player);
    }

    setSelectedPlayers(newSelectedPlayers);
    setPlayers(newPlayers);
  };

  const submitAttribution = async () => {
    if (selectedPlayers.length === 0) {
      return;
    }
    setLoading(true);
    const newActivityId = await attributeActivityExecution(activityData, selectedPlayers);

    if (newActivityId) {
      closeDialog();
    } else {
      //TODO: handle error
    }
  };

  type CheckLastExecutionTimeLimitParam = {
    _created: string;
  };

  const checkLastActivityExecutionTimeLimit = (activityExecution: CheckLastExecutionTimeLimitParam) => {
    return (new Date().getTime() - new Date(activityExecution._created).getTime()) / 1000 / 60 < KEEP_GROUP_TIME_LIMIT;
  };

  const init = async () => {
    setLoading(true);

    if (!program.name) {
      const restorativeProgramResponse = await Restorative.getProgram(activityData.program.id);
      const programName = restorativeProgramResponse.program.name;
      setProgram({ ...program, name: programName });
    }

    const lastActivityExecutionResponse = await getLastActivityExecution();

    if (lastActivityExecutionResponse.activity_execution) {
      const participants = lastActivityExecutionResponse.activity_execution.participants;
      setLastActivityLoaded(true);

      if (checkLastActivityExecutionTimeLimit(lastActivityExecutionResponse.activity_execution)) {
        setSelectedPlayers(participants);
      } else {
        setSelectedPlayers([]);
      }
    } else {
      setLastActivityLoaded(true);
      setSelectedPlayers([]);
    }

    setLoading(false);
  };

  const getPlayers = async (playersToExclude: Player[] = []) => {
    if (!lastActivityLoaded) {
      return;
    }
    setLoadingPlayers(true);
    if (playersToExclude.length === 0) {
      playersToExclude = selectedPlayers;
    }

    const params = { limit: PLAYERS_PER_PAGE, offset: page, players_to_exclude: playersToExclude, query: searchQuery };
    const playersResponse = await Restorative.getPlayers(params);
    setTotalPlayers(playersResponse.total_count);
    setPlayers(playersResponse.players);
    setLoadingPlayers(false);
  };

  useEffect(() => {
    mixpanel.track(MIXPANEL_EVENT, {
      ActivityName: activityData.title,
      Duration: activityData.duration,
      Program: activityData.program,
    });
    init();
  }, []);

  useEffect(() => {
    getPlayers();
  }, [selectedPlayers, page, searchQuery]);

  const title = `Attribute Duration to Participants`;
  const formattedDuration = formatDuration(activityData.duration);
  return (
    <Dialog
      canClose={false}
      closeCallback={() => {
        closeDialog();
      }}
      isOpen={true}
    >
      <Content>
        <Title>{title}</Title>
        <Section>
          {program.name && (
            <InfoCard
              label={'Skill Practiced: '}
              text={program.name}
              type="program"
              programId={activityData.program.id}
            />
          )}
          <InfoCard label={'Activity: '} text={activityData.title} />
          <InfoCard label={'Duration: '} text={formattedDuration} type="time" />
        </Section>

        <ResidentSection>
          <SectionTitle>Residents:</SectionTitle>
          <ResidentSearch
            onSearch={(query) => {
              if (query === searchQuery) {
                return;
              }
              setPage(0);
              setSearchQuery(query);
            }}
          />
          <Subsection>
            <SubsectionTitle>
              Selected Residents: {selectedPlayers.length}{' '}
              {selectedPlayers.length > 0 && (
                <ClearSelectedButton onClick={clearSelectedPlayers}>Clear</ClearSelectedButton>
              )}
            </SubsectionTitle>
            <SelectedPlayerList
              players={selectedPlayers}
              removeSelectedPlayer={(id) => {
                removeSelectedPlayer(id);
              }}
            />
          </Subsection>
          <Subsection>
            <SubsectionTitle>All Residents:</SubsectionTitle>
            <ActivityAttributionPlayerBank
              loadingPlayers={loadingPlayers}
              players={players}
              onPlayerClick={(name, id) => {
                addPlayerToSelected(id);
              }}
            />
            <PaginationContainer>
              <Pagination
                currentPage={page + 1}
                total={totalPlayers}
                displayedResults={PLAYERS_PER_PAGE}
                callback={(newPage) => {
                  setPage(newPage - 1);
                }}
              />
            </PaginationContainer>
          </Subsection>
        </ResidentSection>

        <ButtonContainer>
          {!loading && (
            <>
              <SkipButton onClick={closeDialog}>Skip Attribution</SkipButton>
              <Button disabled={selectedPlayers.length === 0} onClick={submitAttribution}>
                Attribute to Selected Residents
              </Button>
            </>
          )}
        </ButtonContainer>
        {loading && (
          <LoadingMessage>
            Working... <i className="fa fa-spin fa-spinner"></i>
          </LoadingMessage>
        )}
      </Content>
    </Dialog>
  );
};

export default ActivityAttributionDialog;
